import React, { Component } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import fp from 'lodash/fp';
import styled from 'styled-components';
import { connect } from 'react-redux';
import {
  toCurrency,
  getDiscountValue,
  getValue,
  color,
} from '@deerplusofficial/pos-helper';
import actions from '../../actions';
import { withQueryProduct } from '../../model/Queries';
import { itemOfProductModalShape } from '../../model/Types';
import Dimmer from '../Dimmer/Dimmer';
import Modal from '../Modal/Modal';
import InfoSection from '../Info/InfoSection';
import Info from '../Info/Info';
import InfoWithCalculatorInput from '../InfoWithCalculatorInput/InfoWithCalculatorInput';
import Select from '../Select/Select';
import SpecSelector from './SpecSelector';
import Price from './Price';

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
`;

const Title = styled.div`
  font-size: 20px;
`;

const Storage = styled.div`
  font-size: 12px;
  color: ${color.text.secondary};
`;

class ProductModal extends Component {
  static propTypes = {
    productId: PropTypes.string,
    product: itemOfProductModalShape,
    active: PropTypes.bool,
    quantity: PropTypes.number,
    productDiscount: PropTypes.oneOfType([
      PropTypes.shape({
        name: PropTypes.string,
        formattedValue: PropTypes.string,
      }),
      PropTypes.string,
    ]),
    editMode: PropTypes.bool,
    cart: PropTypes.shape({}),
    addCartItem: PropTypes.func,
    editCartItem: PropTypes.func,
    removeCartItem: PropTypes.func,
    onProductFetch: PropTypes.func,
    closeProductModal: PropTypes.func,
  };

  static defaultProps = {
    productId: '',
    product: {},
    active: false,
    quantity: 1,
    productDiscount: {},
    editMode: false,
    cart: {},
    addCartItem: _.identity,
    editCartItem: _.identity,
    removeCartItem: _.identity,
    onProductFetch: _.identity,
    closeProductModal: _.identity,
  };

  state = {
    fetching: true,
    activeCalculator: '',
    discount: {},
    price: undefined,
  };

  componentDidMount() {
    this.onModalInfoUpdate();
  }

  async componentDidUpdate(prevProps, prevState) {
    const { active, onProductFetch } = this.props;

    if (prevProps.active === active || !active) return;

    this.setState({ fetching: true });

    await onProductFetch();

    this.onModalInfoUpdate();
  }

  onModalInfoUpdate = () => {
    const {
      productDiscount,
      product,
      editMode,
      quantity: quantityInCart,
      specs: itemSpecs,
    } = this.props;
    const specs = editMode
      ? itemSpecs
      : _.fromPairs(
          _.map(product.specs, (options, spec) => [spec, options[0]])
        );

    this.setState({
      specs,
      price: this.getItemPrice(specs),
      discount: productDiscount,
      quantity: editMode ? quantityInCart : 1,
      fetching: false,
      activeCalculator: '',
    });
  };

  onSpecSelect = ({ value, label }) => {
    const { specs } = this.state;
    const selectedSpec = { [label]: value };

    this.setState({
      specs: { ...specs, ...selectedSpec },
      price: this.getItemPrice(selectedSpec),
    });
  };

  onPriceCalculatorActive = () =>
    this.setState({ price: 0, activeCalculator: 'price' });

  onPriceChange = formattedValue =>
    this.setState({ price: getValue(formattedValue) });

  onQuantityCalculatorActive = () =>
    this.setState({ activeCalculator: 'quantity', quantity: 0 });

  onQuantityChange = quantity => {
    if (this.checkIfDisabled(quantity)) return;

    this.setState({ quantity });
  };

  onDiscountCalculatorActive = () =>
    this.setState({ activeCalculator: 'discount' });

  onDiscountCalculatorRef = element => (this.discountCalculator = element);

  onDiscountCalculatorKeyDown = e => this.discountCalculator.onButtonPress(e);

  onDiscountChange = formattedValue => {
    const criteria = /%/.test(formattedValue) ? 1 : 0;
    this.setState({
      discount: {
        criteria,
        formattedValue,
        type: 0,
        name: '手動折扣',
      },
    });
  };

  onDiscountReset = () => this.setState({ discount: {} });

  onPlanChange = e => {
    const targetId = e.target.value;

    if (targetId === 'init') return this.setState({ discount: {} });

    const { id, name, formattedValue } = _.find(this.plans, { id: targetId });

    return this.setState({ discount: { id, name, formattedValue, type: 1 } });
  };

  onSave = () => {
    const { discount, quantity, specs, price } = this.state;
    const {
      addCartItem,
      editCartItem,
      closeProductModal,
      editMode,
      productId,
      product: { name },
    } = this.props;
    const { retail_price } = this.item;
    const unMutatedProps = _.omit(this.item, [
      'quantity',
      'discounts',
      'product_id',
      'unit_price',
    ]);
    const unit_price = retail_price === price ? _.noop() : price;
    const save = editMode ? editCartItem : addCartItem;

    const item = {
      productId,
      quantity,
      discount,
      specs,
      unit_price,
      name,
      ...unMutatedProps,
    };

    save(item);
    closeProductModal();
  };

  onRemove = () => {
    const { removeCartItem, closeProductModal } = this.props;

    removeCartItem(this.item.id);
    closeProductModal();
  };

  checkIfDisabled = quantity => {
    if (this.item.order_when_no_stock) return false;

    return this.item.track_quantity && quantity > this.currentStorage;
  };

  getItemPrice = specs => {
    const { cart, product } = this.props;
    const { retail_price, id } = _.find(product.items, { specs }) || {};
    const { unit_price } = _.get(cart.items, [id], {});
    return unit_price || retail_price;
  };

  get actions() {
    const remove = {
      text: '移除此商品',
      callback: this.onRemove,
    };
    const save = {
      text: '儲存',
      callback: this.onSave,
      disabled:
        this.checkIfDisabled(this.state.quantity) || !fp.size(this.item),
    };

    if (this.props.editMode) return { remove, save };

    return { save };
  }

  get discountOptions() {
    const {
      plans,
      state: { quantity, price },
    } = this;
    const options = _.map(plans, ({ name, id, base_amount }) => ({
      text: name,
      value: id,
      disabled: quantity * price < base_amount,
    }));

    return [{ text: '未選擇折扣方案', value: 'init' }, ...options];
  }

  get discountPrice() {
    const {
      quantity,
      price,
      discount: { formattedValue, type },
    } = this.state;

    return getDiscountValue({
      quantity,
      formattedValue,
      price,
      isDiscountPlan: type === 1,
    });
  }

  get subtotalPrice() {
    const { quantity, price } = this.state;

    const subtotal = quantity * price;

    if (subtotal <= this.discountPrice) return 0;

    return subtotal - this.discountPrice;
  }

  get currentPlan() {
    return _.get(this.state.discount, ['id'], 'init');
  }

  get customDiscount() {
    const { type, formattedValue } = this.state.discount;
    const isCustomDiscount = type === 0;

    return isCustomDiscount ? formattedValue : '';
  }

  get item() {
    const {
      product: { items },
    } = this.props;
    const { specs } = this.state;

    return _.find(items, { specs }) || {};
  }

  get plans() {
    return _.concat(
      this.props.product.discounts || [],
      this.item.discounts || []
    );
  }

  get currentStorage() {
    const { cart, editMode } = this.props;
    const { storage, id, order_when_no_stock } = this.item;
    const quantityInCart = _.get(cart.items, [id, 'quantity'], 0);

    if (order_when_no_stock) return storage - quantityInCart;

    return editMode ? storage : storage - quantityInCart;
  }

  get storageText() {
    if (!this.item.order_when_no_stock && this.currentStorage === 0)
      return '無庫存，無法購買';

    return `庫存 ${this.currentStorage} 件`;
  }

  render() {
    const { quantity, fetching, activeCalculator, price } = this.state;
    const { active, closeProductModal, product, editMode } = this.props;

    return (
      <Modal
        active={active}
        title="商品購買設定"
        actions={this.actions}
        onToggleModal={closeProductModal}
      >
        {fetching ? (
          <Dimmer dark />
        ) : (
          <React.Fragment>
            <Header>
              <div>
                <Title>{product.name}</Title>
                {this.item.track_quantity && (
                  <Storage>{this.storageText}</Storage>
                )}
              </div>
              <Price
                value={toCurrency(price)}
                onFocus={this.onPriceCalculatorActive}
                onChange={this.onPriceChange}
                showCalculator={activeCalculator === 'price'}
              />
            </Header>
            {_.map(product.specs, (options, name) => (
              <SpecSelector
                disabled={editMode}
                key={name}
                value={this.state.specs[name]}
                label={name}
                options={options}
                onSelect={this.onSpecSelect}
              />
            ))}
            <InfoSection>
              <InfoWithCalculatorInput
                hasControllers
                label="數量"
                calculatorMode="amount"
                value={quantity}
                onFocus={this.onQuantityCalculatorActive}
                onChange={this.onQuantityChange}
                showCalculator={activeCalculator === 'quantity'}
              />
            </InfoSection>
            <InfoSection>
              <InfoWithCalculatorInput
                advanced
                label="手動輸入折扣"
                addon={_.size(this.customDiscount) > 0 && 'delete'}
                displayValue={_.trim(`- ${this.customDiscount}`)}
                value={this.customDiscount}
                onFocus={this.onDiscountCalculatorActive}
                onAddonClick={this.onDiscountReset}
                onChange={this.onDiscountChange}
                showCalculator={activeCalculator === 'discount'}
              />
            </InfoSection>
            <InfoSection>
              <Info
                label="折扣方案"
                addon={this.currentPlan !== 'init' && 'delete'}
                onAddonClick={this.onDiscountReset}
              >
                <Select
                  noBorder
                  value={this.currentPlan}
                  options={this.discountOptions}
                  onChange={this.onPlanChange}
                />
              </Info>
            </InfoSection>
            <InfoSection>
              <Info label="折扣金額">{toCurrency(-this.discountPrice)}</Info>
              <Info label="小計">{toCurrency(this.subtotalPrice)}</Info>
            </InfoSection>
          </React.Fragment>
        )}
      </Modal>
    );
  }
}

const mapStateToProps = ({ cart, productModal }) => ({
  cart,
  ...productModal,
});
const {
  add: addCartItem,
  edit: editCartItem,
  remove: removeCartItem,
} = actions.cart;
const { close: closeProductModal } = actions.productModal;

export default fp.compose(
  connect(
    mapStateToProps,
    { addCartItem, editCartItem, removeCartItem, closeProductModal }
  ),
  withQueryProduct()
)(ProductModal);



// WEBPACK FOOTER //
// ./src/components/ProductModal/ProductModal.js